/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from "app/shared/lib/essette-site";
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { AuthService } from 'app/um/services/auth-service';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { Redirect } from 'aurelia-router';
import { apiCall } from 'app/shared/lib/api/api';
import { fireEvents } from 'app/shared/lib/fire-events';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import { deepClone } from 'app/shared/lib/utilities';
import { task } from 'app/shared/lib/decorators';
import { getReference } from 'app/shared/models/essette-types';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { AuthExternalSystemChangeEvent, AuthHeaderChangeEvent } from 'app/um/lib/events';
import { CwqiManager, CwqiSavedEvent, CwqiSavedGuidelinesEvent } from 'app/ext/cwqi/cwqi-manager';
import * as UM from 'app/um/models/essette-api';
import { IAuthChangeCustomEventDetail } from 'app/um/lib/events';


@autoinject()
export class AuthCareDays extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected auth: UM.Auth | null = null;
    protected editAuth: UM.Auth | null = null;
    protected dischargeLocations: UM.DischargeLocation[] = [];
    protected readonly: boolean = true;

    protected $isValid!: boolean;
    protected validateForm = validate;

    private subscriptions: Array<Subscription> = [];


    ////****************** Element References ***************************************////
    protected $element!: Element;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly cwqi: CwqiManager,
        private readonly authService: AuthService,
        private readonly admissionCareDayService: AdmissionCareDayService,
        private readonly context: AuthPageContext,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async canActivate(params: { authId: string }): Promise<Redirect | true> {
        if(!params?.authId) { throw new Error('Missing or invalid authId parameter'); }

        // if auth is already loaded onto context and isAdmission is known, check it
        if(this.context.auth.id === params.authId && typeof this.context.auth.isAdmission !== 'undefined') {
            if(!this.context.auth.isAdmission) {
                return new Redirect('');
            }
            return true;
        }
        // otherwise query for isAdmission
        try {
            const response = await this.authService.loadAuth(params.authId, [], undefined, ['isAdmission']);
            const apiAuth = response.results && response.results[0];
            const isAdmission = apiAuth?.isAdmission;
            if(!isAdmission) {
                return new Redirect('');
            }
            return true;
        }
        catch(error) {
            throw error;
        }
    }

    protected async activate(): Promise<void> {
        this.checkPermissions();
        this.loadLookups();

        if(this.context && this.context.auth) {
            this.auth = this.context.auth;
            this.editAuth = deepClone(this.auth);
        }
        else {
            throw new Error('Context not loaded yet!');
        }

        this.subscriptions.push(
            this.events.subscribe(CwqiSavedEvent, () => this.reload()),
            this.events.subscribe(AuthExternalSystemChangeEvent, () => this.reload()),
            this.events.subscribe(AuthHeaderChangeEvent, () => this.reload()),
            this.events.subscribe(CwqiSavedGuidelinesEvent, () => this.reload())
        );
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    @task // TODO: JP | work around flatpicker delegating events syncronously
    protected dischargeDateChanged(): void {
        if(!this.editAuth?.discharge?.date && this.editAuth?.discharge) {
            this.editAuth.discharge.location = undefined;
        }
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const canEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        const canEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.readonly = this.context?.authIsReadOnly(canEditAuth, canEditClosedAuth);
    }

    private async reload(): Promise<void> {
        this.toaster.info().message('Refreshing auth information').show();
        try {
            this.state.beginLoad();
            this.loadLookups();
            await this.context.refreshAuthSettings();
            this.auth = this.context.auth;
            this.editAuth = deepClone(this.auth);
            this.checkPermissions();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    private resetInfo(): void {
        if(this.auth) {
            this.editAuth = deepClone(this.auth);
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginSave();

            const [responseDischargeLocations] = await Promise.all([
                this.admissionCareDayService.loadDischargeLocations(undefined, undefined, ['description'])
            ]);

            this.dischargeLocations = responseDischargeLocations.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async saveInfo(): Promise<void> {
        // if(!this.auth || !this.discharge || !this.discharge.date || !this.discharge.location) { return; }
        if(this.editAuth === null || this.editAuth === undefined) {
            return;
        }

        let saveAuth: UM.Auth = {
            id: this.editAuth.id,
            admission: {
                date: this.editAuth.admission?.date
            },
            discharge: {
                date: this.editAuth.discharge?.date,
                // additional location display info is needed for CWQI Discharge API call
                location: getReference(this.editAuth.discharge?.location, {
                    $display: this.editAuth.discharge?.location?.$display || null
                }) || null
            }
        };

        const externalSystemCode = this.editAuth.externalSystem?.code;
        const externalResultsIdentifier = this.editAuth.number && externalSystemCode === 'CWQI' ? this.cwqi.getEpisodeID(this.editAuth.number) : null;
        if(externalResultsIdentifier) {
            // must provide MCG episodeID for CWQI Discharge API call
            saveAuth.externalSystem = {
                code: externalSystemCode,
                externalResultsIdentifier: externalResultsIdentifier
            }
        };

        try {
            this.state.beginSave();

            const response = await this.authService.saveAuth(saveAuth);

            if(response.isSuccessful) {
                this.toaster.info().message('Admission Information Saved Successfully').show();
                await this.reload();
                const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: false };
                fireEvents(this.$element, ['auth-change'], detail);
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

};
